import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { computeFingerprint } from "@/fingerprint";
import {
  MAX_NAME_LENGTH,
  MIN_PASSWORD_LENGTH,
  SESSION_STORAGE_KEY,
  VERIFICATION_CODE_LENGTH,
  emailError,
  handleError,
  nameError,
  normalizeHandle,
  passwordError,
} from "@shared/protocol";
import type { AuthResult, AuthUser } from "@shared/types";
import { Ajuda } from "@/components/Ajuda";
import { Historia } from "@/components/Historia";
import { BRAND, C, FONT_MONO, FONT_SANS, hexA } from "@/theme";

export type AuthProps = {
  onSession: (token: string, user: AuthUser) => void;
  /** Recado vindo de fora, tipicamente a falha de um retorno do Google. */
  notice?: string | null;
  /** Chamado quando a pessoa mexe na tela, pra o recado não ficar preso. */
  onDismissNotice?: () => void;
};

/** "sobre" não é um modo de autenticação: é a história, na mesma faixa de abas. */
/** "sobre" e "ajuda" não são modos de autenticação: são conteúdo, na mesma faixa. */
type Mode = "entrar" | "criar" | "sobre" | "ajuda";

const TABS: { id: Mode; label: string }[] = [
  { id: "entrar", label: "Entrar" },
  { id: "criar", label: "Criar conta" },
  { id: "sobre", label: "Sobre" },
  { id: "ajuda", label: "Ajuda" },
];

const MONO_LABEL: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".12em",
  color: C.textFaint,
};

const MONO_NOTE: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 9.5,
  lineHeight: 1.7,
  letterSpacing: ".06em",
  color: C.textGhost,
};

/**
 * As mensagens de `@shared/protocol` vivem sem acento (contrato compartilhado);
 * a interface mostra as acentuadas. Texto desconhecido passa direto.
 */
const ACCENTED: Record<string, string> = {
  "Escolha um nome de usuario.": "Escolha um nome de usuário.",
  "Use de 3 a 24 caracteres, apenas letras minusculas, numeros, ponto, hifen ou _.":
    "Use de 3 a 24 caracteres: letras minúsculas, números, ponto, hífen ou _.",
  "Escolha como voce quer aparecer.": "Escolha como você quer aparecer.",
  [`No maximo ${MAX_NAME_LENGTH} caracteres.`]: `No máximo ${MAX_NAME_LENGTH} caracteres.`,
  "Informe seu e-mail.": "Informe seu e-mail.",
  "Esse e-mail nao parece valido.": "Esse e-mail não parece válido.",
};

function pt(message: string | null): string | null {
  if (message === null) return null;
  return ACCENTED[message] ?? message;
}

/* -------------------------------------------------------------------- */
/* Retorno do Google                                                     */
/* -------------------------------------------------------------------- */

/**
 * Códigos que o servidor devolve em `?gerror=` quando o login com Google não
 * fecha (ver `GoogleErrorCode` em `server/googleAuth.ts`). Viram frase aqui,
 * e não na querystring, pra URL não carregar texto acentuado.
 */
const GOOGLE_ERRORS: Record<string, string> = {
  config: "A entrada com o Google não está configurada neste servidor. Use usuário e senha.",
  negado: "A entrada com o Google foi cancelada.",
  estado: "Essa tentativa expirou ou veio de outro lugar. Comece de novo.",
  google: "O Google não confirmou sua conta. Tente de novo em instantes.",
  email:
    "Sua conta do Google não tem um e-mail confirmado, e é por ele que a gente liga as contas. Use usuário e senha.",
  duplicado:
    "Existe mais de uma conta com esse e-mail, então não dá pra saber qual é a sua. Entre com usuário e senha.",
  banido:
    "Esse cadastro não pode ser concluído. Se você acha que isso é um engano, fale com a moderação.",
  limite:
    "Muitas contas novas foram criadas agora há pouco. Aguarde um pouco e tente de novo.",
  servidor: "Algo deu errado do nosso lado. Tente de novo em instantes.",
};

export function googleErrorMessage(code: string): string {
  return GOOGLE_ERRORS[code] ?? GOOGLE_ERRORS.servidor;
}

/* -------------------------------------------------------------------- */
/* Movimento                                                             */
/* -------------------------------------------------------------------- */

/**
 * Altura animada: o cartão cresce e encolhe junto com o conteúdo em vez de
 * saltar quando a etapa troca. A primeira medição não anima (`animate`
 * começa falso), senão o cartão surgiria esticando de zero.
 *
 * Quem pediu menos movimento no sistema não recebe nenhum: a transição vive
 * na classe `.auth-morph`, desligada por `prefers-reduced-motion` no CSS.
 */
function Morph({ children }: { children: ReactNode }) {
  const inner = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ height: 0, animate: false });

  useLayoutEffect(() => {
    const node = inner.current;
    if (!node) return;
    const apply = () => setBox(prev => ({ height: node.offsetHeight, animate: prev.height > 0 }));
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={box.animate ? "auth-morph" : undefined}
      style={{ height: box.height > 0 ? box.height : undefined, overflow: "hidden" }}
    >
      <div ref={inner}>{children}</div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Autoria                                                               */
/* -------------------------------------------------------------------- */

/**
 * Credito discreto de quem desenvolveu o app. O `a` global da folha de estilo e
 * laranja; aqui a cor vem inline para o link nao gritar no rodape do cartao.
 */
function CreditLink() {
  const [hover, setHover] = useState(false);
  return (
    <a
      href="https://lfrcrew.dev"
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        color: hover ? C.accent : C.textLabel,
        textDecoration: hover ? "underline" : "none",
        textUnderlineOffset: 3,
        transition: "color 140ms ease",
      }}
    >
      lfrcrew.dev
    </a>
  );
}

/* -------------------------------------------------------------------- */
/* Resposta do servidor                                                  */
/* -------------------------------------------------------------------- */

/** Corpo de erro do servidor: `{ error: "..." }` em portugues. */
function serverMessage(value: unknown): string | null {
  if (typeof value !== "object" || value === null) return null;
  const message = (value as Record<string, unknown>).error;
  return typeof message === "string" && message.trim() ? message : null;
}

/** `JSON.parse` que devolve `null` em vez de estourar em resposta nao-JSON. */
function readJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/** Resposta de `/api/register`: cadastro guardado como pendente, aguardando o código. */
type PendingRegistration = { pendingId: string; expiresInSec: number; emailSent: boolean };

function parsePending(value: unknown): PendingRegistration | null {
  if (typeof value !== "object" || value === null) return null;
  const body = value as Record<string, unknown>;
  const { pendingId, expiresInSec } = body;
  if (typeof pendingId !== "string" || pendingId.length === 0) return null;
  if (typeof expiresInSec !== "number") return null;
  // Só um `false` explícito conta como falha. Servidor antigo, que não manda
  // o campo, continua sendo tratado como envio normal.
  return { pendingId, expiresInSec, emailSent: body.emailSent !== false };
}

/** Valida o `AuthResult` sem confiar no formato recebido. */
export function parseAuth(value: unknown): AuthResult | null {
  if (typeof value !== "object" || value === null) return null;
  const body = value as Record<string, unknown>;
  const sessionToken = body.sessionToken;
  const rawUser = body.user;
  if (typeof sessionToken !== "string" || typeof rawUser !== "object" || rawUser === null) {
    return null;
  }
  const user = rawUser as Record<string, unknown>;
  if (
    typeof user.id !== "string" ||
    typeof user.name !== "string" ||
    typeof user.handle !== "string"
  ) {
    return null;
  }
  return {
    sessionToken,
    user: {
      id: user.id,
      name: user.name,
      handle: user.handle,
      avatarUrl: typeof user.avatarUrl === "string" ? user.avatarUrl : null,
    },
  };
}

/** Mensagem de reserva quando o servidor nao explica o erro. */
function fallbackMessage(status: number, mode: Mode): string {
  if (status === 401 || status === 403) return "Nome de usuário ou senha incorretos.";
  if (status === 409) return "Esse nome de usuário já está em uso. Escolha outro.";
  if (status === 400) return "Confira os dados preenchidos e tente de novo.";
  if (status === 429) return "Muitas tentativas seguidas. Espere um pouco e tente de novo.";
  const acao = mode === "entrar" ? "entrar" : "criar a conta";
  return `Não foi possível ${acao} agora (erro ${status}).`;
}

export function storeToken(token: string): void {
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, token);
  } catch {
    // Navegacao privada bloqueia o armazenamento: a sessao segue so em memoria.
  }
}

/* -------------------------------------------------------------------- */
/* Aviso                                                                 */
/* -------------------------------------------------------------------- */

function Alert({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        // No topo, e não centralizado: assim que o texto passa de uma linha, a
        // bolinha centralizada flutuava no meio do bloco em vez de marcar o
        // começo da mensagem.
        alignItems: "flex-start",
        gap: 9,
        padding: "9px 11px",
        borderRadius: 9,
        border: `1px solid ${hexA(C.red, 0.35)}`,
        background: hexA(C.red, 0.12),
        fontSize: 12.5,
        lineHeight: 1.45,
        color: C.red,
        animation: "fadein 150ms ease",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          flex: "0 0 auto",
          // Alinha com a primeira linha do texto: (18.1px de linha - 7) / 2.
          marginTop: 5.5,
          borderRadius: 999,
          background: C.red,
        }}
      />
      {/*
        `minWidth: 0` mais a quebra em qualquer ponto: sem os dois, uma palavra
        sem espaços — um e-mail, por exemplo — não cabe nem encolhe, e o item
        flex empurra o cartão inteiro para fora da tela, levando junto as caixas
        do código.
      */}
      <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{children}</span>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Campo                                                                 */
/* -------------------------------------------------------------------- */

type FieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  /** Problema encontrado agora; so aparece quando `showError`. */
  error: string | null;
  showError: boolean;
  type?: "text" | "password";
  placeholder?: string;
  /** Prefixo fixo dentro da caixa, como o arroba do nome de usuario. */
  prefix?: string;
  mono?: boolean;
  maxLength?: number;
  autoComplete: string;
  autoFocus?: boolean;
};

function Field({
  label,
  value,
  onChange,
  onBlur,
  error,
  showError,
  type = "text",
  placeholder,
  prefix,
  mono = false,
  maxLength,
  autoComplete,
  autoFocus = false,
}: FieldProps) {
  const [focused, setFocused] = useState(false);
  const invalid = showError && error !== null;
  const border = invalid ? hexA(C.red, 0.55) : focused ? C.accent : C.lineStrong;

  return (
    <label className="auth-field" style={{ display: "grid", gap: 7 }}>
      <span style={MONO_LABEL}>{label}</span>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: prefix ? "10px 12px" : 0,
          border: `1px solid ${border}`,
          borderRadius: 9,
          background: C.field,
          transition: "border-color 140ms ease",
        }}
      >
        {prefix ? (
          <span
            style={{
              flex: "0 0 auto",
              fontFamily: FONT_MONO,
              fontSize: 13,
              color: C.textGhost,
            }}
          >
            {prefix}
          </span>
        ) : null}
        <input
          value={value}
          onChange={event => onChange(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur();
          }}
          type={type}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          spellCheck={false}
          maxLength={maxLength}
          style={{
            flex: 1,
            minWidth: 0,
            padding: prefix ? 0 : "10px 12px",
            border: 0,
            borderRadius: 9,
            background: "transparent",
            color: C.text,
            fontFamily: mono ? FONT_MONO : FONT_SANS,
            fontSize: mono ? 13 : 14,
            letterSpacing: mono ? ".04em" : "normal",
            outline: 0,
          }}
        />
      </div>
      {invalid ? (
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            lineHeight: 1.5,
            letterSpacing: ".02em",
            color: C.red,
          }}
        >
          {error}
        </span>
      ) : null}
    </label>
  );
}

/* -------------------------------------------------------------------- */
/* Código de verificação                                                 */
/* -------------------------------------------------------------------- */

type CodeInputProps = {
  /**
   * Uma posição por casa, sempre com o mesmo comprimento. Guardar o código
   * como texto simples perderia o buraco do meio: apagar a segunda casa de
   * "123" viraria "13", e o 3 pularia de lugar sozinho na tela.
   */
  slots: string[];
  onChange: (next: string[]) => void;
  /** Disparado quando as seis casas ficam preenchidas, já com o código pronto. */
  onComplete: (value: string) => void;
  disabled: boolean;
  invalid: boolean;
};

/**
 * Uma caixa por dígito. Além de ler melhor que um campo só, resolve o que o
 * campo único não fazia: colar o código inteiro distribui pelas casas, digitar
 * avança sozinho, apagar volta, e o preenchimento automático do SMS/e-mail
 * (`one-time-code`) continua funcionando porque a primeira casa mantém o
 * `autoComplete`.
 */
function CodeInput({ slots, onChange, onComplete, disabled, invalid }: CodeInputProps) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  const focusAt = (index: number): void => {
    const clamped = Math.max(0, Math.min(VERIFICATION_CODE_LENGTH - 1, index));
    const node = boxes.current[clamped];
    node?.focus();
    node?.select();
  };

  /** Escreve `digits` a partir de `at`, mantendo o resto do que já havia. */
  const writeFrom = (at: number, digits: string): void => {
    if (digits.length === 0) return;
    const next = slots.slice();
    for (let offset = 0; offset < digits.length && at + offset < next.length; offset += 1) {
      next[at + offset] = digits[offset];
    }
    onChange(next);
    focusAt(at + digits.length);
    // O código sai daqui pronto: o estado do pai só muda no próximo render, e
    // esperar por ele mandaria o valor anterior pro servidor.
    if (next.every(digit => digit !== "")) onComplete(next.join(""));
  };

  const onKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const next = slots.slice();
      if (next[index] !== "") {
        next[index] = "";
        onChange(next);
      } else if (index > 0) {
        next[index - 1] = "";
        onChange(next);
        focusAt(index - 1);
      }
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusAt(index - 1);
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      focusAt(index + 1);
    }
  };

  const onPaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    const digits = event.clipboardData.getData("text").replace(/\D/g, "");
    if (digits.length === 0) return;
    event.preventDefault();
    writeFrom(index, digits.slice(0, VERIFICATION_CODE_LENGTH - index));
  };

  return (
    <div style={{ display: "grid", gap: 7 }}>
      <span style={MONO_LABEL}>CÓDIGO DE VERIFICAÇÃO</span>
      <div style={{ display: "flex", gap: 8 }}>
        {slots.map((digit, index) => (
          <input
            // A posição É a identidade da caixa: não há reordenação possível.
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            ref={node => {
              boxes.current[index] = node;
            }}
            className="auth-code-box"
            value={digit}
            onChange={event => writeFrom(index, event.target.value.replace(/\D/g, ""))}
            onKeyDown={onKeyDown(index)}
            onPaste={onPaste(index)}
            onFocus={event => event.target.select()}
            disabled={disabled}
            inputMode="numeric"
            // Só a primeira casa aceita mais de um dígito: é nela que o
            // preenchimento automático do código despeja os seis de uma vez, e
            // `writeFrom` distribui pelas outras. Nas demais, `maxLength={1}`
            // faz digitar por cima substituir em vez de empilhar.
            maxLength={index === 0 ? VERIFICATION_CODE_LENGTH : 1}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            autoFocus={index === 0}
            aria-label={`Dígito ${index + 1} de ${VERIFICATION_CODE_LENGTH}`}
            style={{
              flex: 1,
              minWidth: 0,
              padding: "12px 0",
              border: `1px solid ${invalid ? hexA(C.red, 0.55) : digit ? C.accent : C.lineStrong}`,
              borderRadius: 9,
              background: C.field,
              color: C.text,
              fontFamily: FONT_MONO,
              fontSize: 19,
              fontWeight: 600,
              textAlign: "center",
              outline: 0,
              transition: "border-color 160ms ease, background 160ms ease",
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Botão do Google                                                       */
/* -------------------------------------------------------------------- */

/** Marca oficial do Google, nas quatro cores — exigência da identidade deles. */
function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/**
 * No app desktop o Google recusa autenticação dentro de janela embutida
 * (`disallowed_useragent`). Por isso ali o fluxo abre no navegador do sistema
 * — `window.open` é interceptado pelo `setWindowOpenHandler` do Electron — e a
 * sessão volta pelo endereço `lfrcord://`. No navegador comum é uma navegação
 * normal, sem popup nenhum.
 */
function startGoogle(): void {
  const desktop = window.lfrcordDesktop?.isElectron === true;
  const url = `/api/auth/google/start?target=${desktop ? "desktop" : "web"}`;
  if (desktop) window.open(url, "_blank", "noopener");
  else window.location.assign(url);
}

function GoogleButton({ disabled }: { disabled: boolean }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      className="act-press auth-field"
      onClick={startGoogle}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        width: "100%",
        padding: "10px 0",
        border: `1px solid ${hover && !disabled ? "#FFFFFF" : hexA("#FFFFFF", 0.75)}`,
        borderRadius: 10,
        cursor: disabled ? "default" : "pointer",
        background: hover && !disabled ? "#FFFFFF" : "#F3F5F9",
        color: "#1F1F1F",
        fontFamily: FONT_SANS,
        fontSize: 13.5,
        fontWeight: 600,
        opacity: disabled ? 0.55 : 1,
        transition: "background 150ms ease, border-color 150ms ease, opacity 150ms ease",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
    >
      <GoogleMark />
      Entrar com o Google
    </button>
  );
}

function Divider() {
  return (
    <div
      className="auth-field"
      style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0" }}
      aria-hidden="true"
    >
      <div style={{ flex: 1, height: 1, background: C.lineSoft }} />
      <span style={{ ...MONO_NOTE, color: C.textGhost }}>OU</span>
      <div style={{ flex: 1, height: 1, background: C.lineSoft }} />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Entrada                                                               */
/* -------------------------------------------------------------------- */

/** Depois do formulario de cadastro, falta so confirmar o codigo recebido por e-mail. */
type Step = "form" | "verify";

export function Auth({ onSession, notice = null, onDismissNotice }: AuthProps) {
  const [mode, setMode] = useState<Mode>("entrar");
  const [step, setStep] = useState<Step>("form");
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [touchedName, setTouchedName] = useState(false);
  const [touchedHandle, setTouchedHandle] = useState(false);
  const [touchedPassword, setTouchedPassword] = useState(false);
  const [touchedEmail, setTouchedEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingRegistration | null>(null);
  const [codeSlots, setCodeSlots] = useState<string[]>(() =>
    Array.from({ length: VERIFICATION_CODE_LENGTH }, () => ""),
  );
  /** Honeypot: ninguém navegando de verdade vê ou preenche este campo. */
  const [website, setWebsite] = useState("");
  /** `null` enquanto não sabemos: o botão do Google só entra depois da resposta. */
  const [googleReady, setGoogleReady] = useState<boolean | null>(null);

  const creating = mode === "criar";
  /** "sobre" mostra a história no lugar do formulário — não é login nem cadastro. */
  const sobre = mode === "sobre";
  const ajuda = mode === "ajuda";
  /** Qualquer aba que mostra conteúdo em vez do formulário. */
  const conteudo = sobre || ajuda;
  const indiceAba = Math.max(0, TABS.findIndex(tab => tab.id === mode));

  // Se o servidor não tem as chaves do Google configuradas, o botão nem
  // aparece — melhor não existir do que existir e falhar no clique.
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/providers")
      .then(response => (response.ok ? response.json() : null))
      .then(body => {
        if (alive) setGoogleReady(Boolean(body && (body as Record<string, unknown>).google));
      })
      .catch(() => {
        if (alive) setGoogleReady(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  /** Qualquer erro na tela some no primeiro toque em qualquer campo. */
  const clearProblems = useCallback((): void => {
    setError(null);
    if (notice !== null) onDismissNotice?.();
  }, [notice, onDismissNotice]);

  // No cadastro valem as regras do protocolo. Para entrar basta ter os dois
  // campos preenchidos: quem ja tem conta nao pode ser barrado aqui por uma
  // regra que endureceu depois, e o servidor so exige isso no `/api/login`.
  const nameProblem = creating ? pt(nameError(name)) : null;
  const handleProblem = creating
    ? pt(handleError(handle))
    : handle.length === 0
      ? "Informe o seu nome de usuário."
      : null;
  const passwordProblem = creating
    ? pt(passwordError(password))
    : password.length === 0
      ? "Informe a sua senha."
      : null;
  const emailProblem = creating ? pt(emailError(email)) : null;
  const blocked =
    nameProblem !== null ||
    handleProblem !== null ||
    passwordProblem !== null ||
    emailProblem !== null;

  function switchTo(next: Mode): void {
    if (next === mode || busy) return;
    setMode(next);
    clearProblems();
    setTouchedName(false);
    setTouchedHandle(false);
    setTouchedPassword(false);
    setTouchedEmail(false);
  }

  const clearCode = useCallback((): void => {
    setCodeSlots(Array.from({ length: VERIFICATION_CODE_LENGTH }, () => ""));
  }, []);

  function backToForm(): void {
    if (busy) return;
    setStep("form");
    setPending(null);
    clearCode();
    setError(null);
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setTouchedName(true);
    setTouchedHandle(true);
    setTouchedPassword(true);
    setTouchedEmail(true);
    if (blocked) {
      setError(null);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (creating) {
        const fingerprint = await computeFingerprint();
        const response = await fetch("/api/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.trim(),
            handle: normalizeHandle(handle),
            password,
            email: email.trim(),
            website,
            fingerprint,
          }),
        });
        const payload = readJson(await response.text());
        if (!response.ok) {
          setError(pt(serverMessage(payload)) ?? fallbackMessage(response.status, mode));
          return;
        }
        const started = parsePending(payload);
        if (!started) {
          setError("O servidor respondeu em um formato inesperado.");
          return;
        }
        setPending(started);
        setStep("verify");
        return;
      }

      const response = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ handle: normalizeHandle(handle), password }),
      });
      const payload = readJson(await response.text());
      if (!response.ok) {
        setError(pt(serverMessage(payload)) ?? fallbackMessage(response.status, mode));
        return;
      }
      const session = parseAuth(payload);
      if (!session) {
        setError("O servidor respondeu em um formato inesperado.");
        return;
      }
      storeToken(session.sessionToken);
      onSession(session.sessionToken, session.user);
    } catch {
      setError("Sem resposta do servidor. Verifique se o LFRCORD está no ar.");
    } finally {
      setBusy(false);
    }
  }

  const submitCode = useCallback(
    async (typed: string): Promise<void> => {
      if (busy || !pending || typed.trim().length !== VERIFICATION_CODE_LENGTH) return;

      setBusy(true);
      setError(null);
      // `session` so recebe valor quando a resposta e valida; o `onSession` fica
      // fora do try para que um erro do pai nao vire "sem resposta do servidor".
      let session: AuthResult | null = null;
      try {
        const response = await fetch("/api/register/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pendingId: pending.pendingId, code: typed.trim() }),
        });
        const payload = readJson(await response.text());
        if (!response.ok) {
          setError(pt(serverMessage(payload)) ?? fallbackMessage(response.status, "criar"));
          // Código recusado: limpa as casas pra próxima tentativa começar do zero.
          clearCode();
          return;
        }
        session = parseAuth(payload);
        if (!session) {
          setError("O servidor respondeu em um formato inesperado.");
          return;
        }
      } catch {
        setError("Sem resposta do servidor. Verifique se o LFRCORD está no ar.");
        return;
      } finally {
        setBusy(false);
      }

      storeToken(session.sessionToken);
      onSession(session.sessionToken, session.user);
    },
    [busy, clearCode, onSession, pending],
  );

  const shown = error ?? notice;
  const codeComplete = codeSlots.every(digit => digit !== "");

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: C.app,
        color: C.text,
        fontFamily: FONT_SANS,
      }}
    >
      <form
        onSubmit={event => {
          event.preventDefault();
          if (step === "verify") void submitCode(codeSlots.join(""));
          else void submit(event);
        }}
        style={{
          width: "min(400px,92vw)",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 16,
          background: C.stage,
          padding: "26px 24px 24px",
          animation: "pop 220ms ease",
        }}
      >
        {/* Honeypot: invisível e inalcançável pra quem navega de verdade (mouse, teclado, leitor de tela) — só um preenchedor automático encontra e preenche este campo. */}
        <div
          aria-hidden="true"
          style={{ position: "absolute", left: -9999, width: 1, height: 1, overflow: "hidden" }}
        >
          <label htmlFor="website">Site</label>
          <input
            id="website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={event => setWebsite(event.target.value)}
          />
        </div>

        {/*
          A logo de verdade, não mais a letra "L" desenhada numa caixa azul.
          Como a arte já traz o símbolo e a palavra, o cabeçalho não repete o
          nome ao lado — repetir seria escrever "LFRCORD" duas vezes.
        */}
        <div style={{ display: "grid", gap: 6, justifyItems: "start" }}>
          <img
            src={BRAND.full}
            alt="LFRCORD"
            style={{ height: 44, width: "auto", maxWidth: "100%", display: "block" }}
          />
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 9,
              letterSpacing: ".12em",
              color: C.textLabel,
            }}
          >
            LFR CREW · COMUNIDADE
          </div>
        </div>

        <div style={{ height: 1, margin: "20px 0 16px", background: C.lineSoft }} />

        <Morph>
          {step === "verify" ? (
            <div key="verify" className="auth-step" style={{ display: "grid", gap: 14 }}>
              {/*
                Quando o servidor avisa que o envio falhou, a tela para de
                prometer um e-mail. Pedir um código que o servidor já sabe que
                não saiu deixa a pessoa esperando por nada.
              */}
              {pending && !pending.emailSent ? (
                <div style={{ display: "grid", gap: 10 }}>
                  <Alert>
                    Não conseguimos enviar o e-mail para <strong>{email.trim()}</strong>.
                  </Alert>
                  <div style={{ fontSize: 12.5, lineHeight: 1.6, color: C.textLabel }}>
                    Seu cadastro ficou guardado. Peça o código de {VERIFICATION_CODE_LENGTH}{" "}
                    dígitos a alguém da crew e confirme abaixo.
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 13, lineHeight: 1.6, color: C.textBody }}>
                  Mandamos um código de {VERIFICATION_CODE_LENGTH} dígitos pro e-mail{" "}
                  <strong style={{ color: C.textStrong }}>{email.trim()}</strong>. Confirme abaixo
                  pra terminar o cadastro.
                </div>
              )}

              <CodeInput
                slots={codeSlots}
                onChange={next => {
                  setCodeSlots(next);
                  if (error) setError(null);
                }}
                // Seis dígitos preenchidos já são a intenção inteira: não há
                // por que exigir mais um clique no botão.
                onComplete={typed => void submitCode(typed)}
                disabled={busy}
                invalid={shown !== null}
              />

              {shown ? <Alert>{shown}</Alert> : null}

              <button
                type="submit"
                className="hov-bright-108 act-press"
                disabled={busy || !codeComplete}
                style={{
                  padding: "11px 0",
                  border: 0,
                  borderRadius: 10,
                  cursor: busy || !codeComplete ? "default" : "pointer",
                  background: C.accent,
                  color: C.ink,
                  fontSize: 13.5,
                  fontWeight: 700,
                  opacity: busy || !codeComplete ? 0.6 : 1,
                  transition: "opacity 150ms ease",
                }}
              >
                {busy ? "Confirmando…" : "Confirmar código"}
              </button>
              <button
                type="button"
                onClick={backToForm}
                disabled={busy}
                style={{
                  padding: "9px 0",
                  border: 0,
                  borderRadius: 10,
                  background: "transparent",
                  color: C.textMuted,
                  fontSize: 12,
                  cursor: busy ? "default" : "pointer",
                }}
              >
                Errei algo, voltar
              </button>
            </div>
          ) : (
            <div key="form" className="auth-step">
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  gap: 4,
                  padding: 4,
                  border: `1px solid ${C.lineSoft}`,
                  borderRadius: 11,
                  background: C.field,
                }}
              >
                {/* A pilha do selecionado desliza entre as abas em vez de piscar de um lado pro outro. */}
                <div
                  aria-hidden="true"
                  className="auth-tab-pill"
                  style={{
                    position: "absolute",
                    top: 4,
                    bottom: 4,
                    left: 4,
                    width: `calc(${100 / TABS.length}% - ${(4 * (TABS.length + 1)) / TABS.length}px)`,
                    borderRadius: 8,
                    background: C.selected,
                    transform: `translateX(calc(${indiceAba * 100}% + ${indiceAba * 4}px))`,
                  }}
                />
                {TABS.map(tab => {
                  const active = tab.id === mode;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => switchTo(tab.id)}
                      disabled={busy}
                      style={{
                        position: "relative",
                        flex: 1,
                        padding: "8px 0",
                        border: 0,
                        borderRadius: 8,
                        cursor: busy ? "default" : "pointer",
                        background: "transparent",
                        color: active ? C.text : C.textMuted,
                        fontSize: 13.5,
                        fontWeight: 600,
                        transition: "color 160ms ease",
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {conteudo && (
                <div style={{ marginTop: 18, maxHeight: "56vh", overflowY: "auto" }}>
                  {sobre ? <Historia compacto /> : <Ajuda />}
                </div>
              )}

              <div
                style={{
                  display: conteudo ? "none" : "grid",
                  gap: 14,
                  marginTop: 18,
                }}
              >
                {creating ? (
                  <Field
                    key="criar-nome"
                    label="NOME DE EXIBIÇÃO"
                    value={name}
                    onChange={value => {
                      setName(value);
                      clearProblems();
                    }}
                    onBlur={() => setTouchedName(true)}
                    error={nameProblem}
                    showError={touchedName}
                    placeholder="como você aparece na crew"
                    maxLength={MAX_NAME_LENGTH}
                    autoComplete="nickname"
                    autoFocus
                  />
                ) : null}

                <Field
                  key={creating ? "criar-handle" : "entrar-handle"}
                  label="NOME DE USUÁRIO"
                  value={handle}
                  onChange={value => {
                    setHandle(normalizeHandle(value));
                    clearProblems();
                  }}
                  onBlur={() => setTouchedHandle(true)}
                  error={handleProblem}
                  showError={touchedHandle}
                  placeholder="seunome"
                  prefix="@"
                  mono
                  maxLength={24}
                  autoComplete="username"
                  autoFocus={!creating}
                />

                {creating ? (
                  <Field
                    key="criar-email"
                    label="E-MAIL"
                    value={email}
                    onChange={value => {
                      setEmail(value);
                      clearProblems();
                    }}
                    onBlur={() => setTouchedEmail(true)}
                    error={emailProblem}
                    showError={touchedEmail}
                    type="text"
                    placeholder="usado só pra confirmar a conta"
                    autoComplete="email"
                  />
                ) : null}

                <Field
                  key={creating ? "criar-senha" : "entrar-senha"}
                  label="SENHA"
                  value={password}
                  onChange={value => {
                    setPassword(value);
                    clearProblems();
                  }}
                  onBlur={() => setTouchedPassword(true)}
                  error={passwordProblem}
                  showError={touchedPassword}
                  type="password"
                  placeholder={creating ? `pelo menos ${MIN_PASSWORD_LENGTH} caracteres` : "sua senha"}
                  autoComplete={creating ? "new-password" : "current-password"}
                />
              </div>

              {shown ? (
                <div style={{ marginTop: 14 }}>
                  <Alert>{shown}</Alert>
                </div>
              ) : null}

              <button
                type="submit"
                className="hov-bright-108 act-press"
                disabled={busy}
                style={{
                  // Na aba "Sobre" nao ha o que enviar.
                  display: conteudo ? "none" : "block",
                  width: "100%",
                  marginTop: 16,
                  padding: "11px 0",
                  border: 0,
                  borderRadius: 10,
                  cursor: busy ? "default" : "pointer",
                  background: C.accent,
                  color: C.ink,
                  fontSize: 13.5,
                  fontWeight: 700,
                  opacity: busy ? 0.7 : 1,
                  transition: "opacity 150ms ease, transform 120ms ease",
                }}
              >
                {busy
                  ? creating
                    ? "Criando conta…"
                    : "Entrando…"
                  : creating
                    ? "Criar conta"
                    : "Entrar"}
              </button>

              {googleReady && !conteudo ? (
                <>
                  <Divider />
                  <GoogleButton disabled={busy} />
                </>
              ) : null}

              <div style={{ ...MONO_NOTE, marginTop: 14 }}>
                {conteudo ? (
                  <>
                    LFRCORD · UM APLICATIVO DA LFR CREW
                    <br />ABRA A ABA ENTRAR OU CRIAR CONTA PARA COMECAR
                  </>
                ) : creating ? (
                  <>
                    A PRIMEIRA CONTA CRIADA VIRA FUNDADOR DA COMUNIDADE
                    <br />
                    AS SEGUINTES ENTRAM COMO CREW
                  </>
                ) : (
                  <>
                    AINDA NÃO TEM CONTA? ABRA A ABA CRIAR CONTA
                    <br />A SESSÃO FICA GUARDADA NESTE NAVEGADOR
                  </>
                )}
              </div>
            </div>
          )}
        </Morph>

        <div style={{ height: 1, margin: "16px 0 12px", background: C.lineSoft }} />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            fontFamily: FONT_MONO,
            fontSize: 9.5,
            letterSpacing: ".06em",
            color: C.textGhost,
          }}
        >
          desenvolvido por <CreditLink />
        </div>
      </form>
    </div>
  );
}
